import { useEffect, useState } from 'react';
import { formatMsUTCToHM } from '@/utilities/timeUtils';
import {
  AreaPlot,
  ChartDataProviderPro,
  ChartsClipPath,
  ChartsGrid,
  ChartsSurface,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  MarkPlot,
} from '@mui/x-charts-pro';
import {
  TachographDataDuringFromToPeriod,
  TachographState,
  TachographStateWithTime,
  TachographBorderCrossings,
} from './TachographService';
import { CustomToolbar } from './CustomToolbar';
import { Row, series } from './TachographDiagramTypes';
import { TachographNoDataIcon } from './TachographNoDataIcon';
import { LineChartYIcons } from './LineChartYIcons';
import { BorderCrossingsLayer } from './BorderCrossingsLayer';
import { CustomChartItemTooltip } from './CustomChartItemTooltip';

export function TachographDiagram({
  data,
  index,
  selectedIndex,
}: {
  data: TachographDataDuringFromToPeriod;
  index: number;
  selectedIndex?: number;
}) {
  const [noCardEvents, setNoCardEvents] = useState<{ from: number; to: number }[]>([]);
  const [chartKey, setChartKey] = useState(`chart-${index}`);
  const zoomed = selectedIndex === index || selectedIndex === undefined;
  const isPhoneView = selectedIndex === undefined;

  useEffect(() => {
    if (zoomed) {
      setChartKey(`chart-${index}-zoomed`);
    }
  }, [zoomed]);

  if (!data.states) return;

  const { times, timelinePercents } = timeReleatedCounting(data.states);
  const states = data.states.map((s) => s.state);
  const ds = buildTachoDataset(states, timelinePercents);
  const borderCrossings: TachographBorderCrossings[] = data.borderCrossings ?? [];

  function timeReleatedCounting(data: TachographStateWithTime[]) {
    const states: TachographState[] = [];
    const times: number[] = [data[0].startInDayMillis];
    const timelinePercents: number[] = [0];
    data.map((d, i) => {
      const TOTAL_MINUTES = 24 * 60;
      const start = d.startInDayMillis;
      const end = d.stopInDayMillis;
      const durationMin = (end - start) / 1000 / 60;
      const widthPercent = durationMin / TOTAL_MINUTES;

      states[i] = TachographState[d.state];
      times.push(end);
      timelinePercents.push(timelinePercents[timelinePercents.length - 1] + 24 * widthPercent);
    });
    return { times, timelinePercents };
  }

  function buildTachoDataset(states: TachographState[], edges: number[]): Row[] {
    if (edges.length !== states.length + 1) return [];

    setNoCardEvents([]);
    const rows: Row[] = [];
    const keyOf: Record<TachographState, keyof Row> = {
      NO_CARD: 'no_card',
      REST: 'rest',
      AVAILABILITY: 'availability',
      WORK: 'work',
      DRIVING: 'driving',
    };
    function emptyRow(x: number): Row {
      return {
        x,
        no_card: null,
        rest: null,
        availability: null,
        work: null,
        driving: null,
      };
    }
    const STATE_Y: Record<TachographState, number> = {
      NO_CARD: 0,
      REST: 1,
      AVAILABILITY: 2,
      WORK: 3,
      DRIVING: 4,
    };

    for (let i = 0; i < states.length; i++) {
      const x0 = edges[i];
      const x1 = edges[i + 1];
      const s = states[i];
      const y = STATE_Y[s];
      const k = keyOf[s];
      if (s === TachographState.NO_CARD) {
        setNoCardEvents((prev) => (prev ? [{ from: x0, to: x1 }, ...prev] : [{ from: x0, to: x1 }]));
      }

      const r0 = emptyRow(x0);
      r0[k] = y;
      rows.push(r0);

      const r1 = emptyRow(x1);
      r1[k] = y;
      rows.push(r1);

      rows.push(emptyRow(x1));
    }

    return rows;
  }

  function summaryAxisFormatter(value: number): string {
    if (!data.summary) return '';
    switch (value) {
      case 1:
        return formatMsUTCToHM(data.summary.restTimeMillis);
      case 2:
        return formatMsUTCToHM(data.summary.availabilityTimeMillis);
      case 3:
        return formatMsUTCToHM(data.summary.workTimeMillis);
      case 4:
        return formatMsUTCToHM(data.summary.drivingTimeMillis);
      default:
        return '';
    }
  }

  function timelineAxisFormatter(value: number): string {
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  function getNumberOfHours() {
    const currentNumberOfHours = times[times.length - 1] / 1000 / 3600;
    if (currentNumberOfHours < 23 || (currentNumberOfHours > 23 && currentNumberOfHours <= 24)) return 24;
    else return currentNumberOfHours;
  }

  return (
    <ChartDataProviderPro
      key={chartKey}
      dataset={ds}
      xAxis={[
        {
          dataKey: 'x',
          zoom: zoomed,
          valueFormatter: timelineAxisFormatter,
          height: 70,
          max: getNumberOfHours(),
        },
      ]}
      yAxis={[
        {
          position: isPhoneView ? 'none' : 'left',
          zoom: false,
          valueFormatter: summaryAxisFormatter,
          min: 0,
          max: 4,
        },
      ]}
      series={series()}
      height={isPhoneView ? 165 : 240}
      margin={{ bottom: -20, top: 25, ...(isPhoneView && { left: 0, right: 0 }) }}
    >
      {isPhoneView && <CustomToolbar />}
      <ChartsSurface>
        <g clipPath={`url(#plotAreaClip)`}>
          <AreaPlot />
          <MarkPlot />
          <ChartsGrid />
          <LinePlot />
          <TachographNoDataIcon noCardEvents={noCardEvents} />
        </g>
        <ChartsClipPath id="plotAreaClip" />
        <ChartsXAxis tickLabelStyle={{ angle: -45, textAnchor: 'end' }} />
        <ChartsYAxis />
        <LineChartYIcons isPhoneView={isPhoneView} summaryValue={summaryAxisFormatter} />
        {borderCrossings && <BorderCrossingsLayer borderCrossings={borderCrossings} />}
        <CustomChartItemTooltip times={times} states={states} isPhoneView={isPhoneView} />
      </ChartsSurface>
    </ChartDataProviderPro>
  );
}
